import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { tokens } from '../../theme';
import { useSettings } from '../../contexts/SettingsContext';
import {
  getADPRound,
  getBlockedReason,
  getKeeperRound,
  getKeeperValue,
  getLastYearDraftRound,
  isBlockedByRules,
} from './keeperRules';

const KeeperTeamDetailModal = ({ open, onClose, owner, keeperPlayers = [], leagueSettings }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { positionColors } = useSettings();

  // Environment-specific debugging helper
  const debugKeeperSettings = (settings, context = '') => {
    const envInfo = {
      environment: process.env.NODE_ENV,
      context,
      settings: {
        keeper_max_years_kept: settings?.keeper_max_years_kept,
        keeper_highest_round: settings?.keeper_highest_round,
        keeper_round_cost: settings?.keeper_round_cost,
        keeper_undrafted_round: settings?.keeper_undrafted_round
      },
      defaults_applied: {
        keeper_max_years_kept: settings?.keeper_max_years_kept ?? 2,
        keeper_highest_round: settings?.keeper_highest_round ?? 3,
        keeper_round_cost: settings?.keeper_round_cost ?? 2,
        keeper_undrafted_round: settings?.keeper_undrafted_round ?? 10
      }
    };
    
    // console.log(`[KeeperTeamDetailModal] ${context} Environment Debug:`, envInfo);
    
    // Specifically flag missing undrafted round setting
    if (!settings?.keeper_undrafted_round) {
      console.warn(`[KeeperTeamDetailModal] 🚨 PRODUCTION ISSUE: keeper_undrafted_round is ${settings?.keeper_undrafted_round}, defaulting to 10`);
    }
    
    return envInfo;
  };

  // Early return if colors are not properly initialized
  if (!colors || !colors.primary) {
    return null;
  }

  // Debug keeper settings on component load
  debugKeeperSettings(leagueSettings, 'Component Load');

  // Helper functions to access nested player data and calculate values
  const getPlayerName = (player) => {
    return player.player?.name || player.name || `Player ${player.sleeper_id}`;
  };
  
  const getPlayerPosition = (player) => {
    return player.player?.position || player.position;
  };
  
  const getPlayerTeam = (player) => {
    return player.player?.team || player.team;
  };
  
  // Undrafted is a meaningful answer here, not missing data: it's the reason a player
  // falls back to the league's undrafted keeper round.
  const formatDraftRound = (value) => {
    if (value === null || value === undefined) return 'Undrafted';
    return `R${value}`;
  };

  // All players share the same draft season; read it off the first that reports one.
  const lastDraftSeasonLabel =
    keeperPlayers.find((p) => p.last_year_draft_season)?.last_year_draft_season ??
    'last season';

  // Get team count for ADP calculation
  const teamCount = leagueSettings?.total_rosters || 12;

  const keeperValueOf = (player) => getKeeperValue(player, { teamCount });

  // Sort players by keeper value (highest value first, non-keepable last)
  const sortedPlayers = [...keeperPlayers].sort((a, b) => {
    const valueA = keeperValueOf(a);
    const valueB = keeperValueOf(b);

    // If neither can be priced, maintain original order
    if (valueA === null && valueB === null) return 0;

    // Unpriceable players go to the end
    if (valueA === null) return 1;
    if (valueB === null) return -1;
    
    // Sort by keeper value (higher is better)
    return valueB - valueA;
  });

  // Calculate keeper stats
  const keepablePlayersList = keeperPlayers.filter((player) => getKeeperRound(player) !== null);
  const keeperCount = keepablePlayersList.length;
  const totalPlayersCount = keeperPlayers.length;
  const totalKeeperValue = keepablePlayersList.reduce((sum, player) => {
    const keeperValue = keeperValueOf(player);
    // Include only positive values
    const value = keeperValue > 0 ? keeperValue : 0;
    return sum + value;
  }, 0);

  // Get position color helper
  const getPositionColor = (position) => {
    if (!positionColors) {
      const fallbackColors = {
        QB: "rgba(239, 116, 161, 0.8)",
        RB: "rgba(143, 242, 202, 0.8)",
        WR: "rgba(86, 201, 248, 0.8)",
        TE: "rgba(254, 174, 88, 0.8)",
        K: "rgba(182, 185, 255, 0.8)",
        DEF: "rgba(191, 117, 93, 0.8)",
        DST: "rgba(191, 117, 93, 0.8)",
      };
      return fallbackColors[position] || "rgba(128, 128, 128, 0.8)";
    }
    return positionColors[position] || positionColors["DEFAULT"] || "rgba(128, 128, 128, 0.8)";
  };

  // Format values for display
  const formatValue = (value, fallback = "N/A") => {
    if (!value || value === "0" || value === 0) return fallback;
    return value;
  };

    // Format values for display
  const formatKeeperRound = (round) => {
    if (!round) return "Not Keepable";
    return `Round ${round}`;
  };

  const formatADPValue = (value) => {
    if (!value || value === "0" || value === 0) return "N/A";
    return `Round ${value}`;
  };

  // Shared by every header cell. Sticky header cells need their own background:
  // MUI takes them out of flow, so a colour set on the TableRow no longer paints
  // behind them and rows would scroll through in the gap.
  const headerCellSx = {
    color: colors.grey[100],
    fontWeight: 600,
    backgroundColor: colors.primary[600],
    borderBottom: `1px solid ${colors.grey[700]}`,
    whiteSpace: 'nowrap',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          minHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, backgroundColor: colors.primary[500] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: colors.blueAccent[500],
                mr: 2,
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                {owner?.display_name || owner?.username || 'Unknown Owner'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  label={`${keeperCount} Keepable Players`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(colors.greenAccent[500], 0.2),
                    color: colors.greenAccent[400],
                  }}
                />
                <Chip
                  label={`${totalPlayersCount} Total Players`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(colors.grey[500], 0.2),
                    color: colors.grey[300],
                  }}
                />
                <Chip
                  label={`Total Value: ${totalKeeperValue.toFixed(1)}`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(colors.blueAccent[500], 0.2),
                    color: colors.blueAccent[400],
                  }}
                />
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: colors.grey[500],
              '&:hover': { color: colors.grey[300] }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {totalPlayersCount === 0 ? (
          <Typography
            variant="h6"
            sx={{
              color: colors.grey[500],
              textAlign: 'center',
              fontStyle: 'italic',
              py: 4,
            }}
          >
            No players found for this team
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: colors.primary[500], flex: 1, minHeight: 0 }}
          >
            <Table stickyHeader sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Player</TableCell>
                  <TableCell sx={headerCellSx}>
                    {`Drafted (${lastDraftSeasonLabel})`}
                  </TableCell>
                  <TableCell sx={headerCellSx}>Keeper Round</TableCell>
                  <TableCell sx={headerCellSx}>Keeper Value</TableCell>
                  <TableCell sx={headerCellSx}>ADP Round</TableCell>
                  <TableCell sx={headerCellSx}>Years Kept</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPlayers.map((player, index) => (
                  <TableRow
                    key={player.sleeper_id || player.player_id || index}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: alpha(colors.primary[400], 0.5),
                      },
                      '&:hover': {
                        backgroundColor: alpha(getPositionColor(getPlayerPosition(player)), 0.1),
                      },
                    }}
                  >
                    <TableCell sx={{ color: colors.grey[100] }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {getPlayerName(player)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        {getPlayerPosition(player) && (
                          <Chip
                            label={getPlayerPosition(player)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              backgroundColor: alpha(getPositionColor(getPlayerPosition(player)), 0.8),
                              color: colors.primary[900],
                              fontWeight: 700,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                        <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 500 }}>
                          {getPlayerTeam(player) || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      <Chip
                        label={formatDraftRound(getLastYearDraftRound(player))}
                        size="small"
                        title={
                          getLastYearDraftRound(player) === null
                            ? `Undrafted in ${lastDraftSeasonLabel}`
                            : [
                                `Round ${getLastYearDraftRound(player)}`,
                                player.last_year_draft_pick_no && `pick ${player.last_year_draft_pick_no}`,
                                player.last_year_picked_by && `by ${player.last_year_picked_by}`,
                              ].filter(Boolean).join(' · ')
                        }
                        sx={{
                          backgroundColor: getLastYearDraftRound(player) !== null
                            ? alpha(colors.blueAccent[500], 0.2)
                            : alpha(colors.grey[500], 0.2),
                          color: getLastYearDraftRound(player) !== null
                            ? colors.blueAccent[300]
                            : colors.grey[400],
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      {getKeeperRound(player) !== null ? (
                        <Chip
                          label={formatKeeperRound(getKeeperRound(player))}
                          size="small"
                          sx={{
                            backgroundColor: alpha(colors.greenAccent[500], 0.2),
                            color: colors.greenAccent[400],
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Chip
                          label="N/A"
                          size="small"
                          sx={{
                            backgroundColor: alpha(colors.grey[500], 0.2),
                            color: colors.grey[400],
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      {keeperValueOf(player) !== null ? (
                        <Typography
                          sx={{
                            color: keeperValueOf(player) > 0 ? colors.greenAccent[400] :
                                   keeperValueOf(player) === 0 ? colors.grey[300] : colors.redAccent[400],
                            fontWeight: 600,
                          }}
                        >
                          {keeperValueOf(player) > 0 ? `+${keeperValueOf(player)}` : keeperValueOf(player)} rounds
                        </Typography>
                      ) : (
                        <Chip
                          label="N/A"
                          size="small"
                          sx={{
                            backgroundColor: alpha(colors.grey[500], 0.2),
                            color: colors.grey[400],
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      <Chip
                        label={formatADPValue(getADPRound(player, teamCount))}
                        size="small"
                        sx={{
                          backgroundColor: alpha(colors.blueAccent[500], 0.2),
                          color: colors.blueAccent[400],
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      {formatValue(player.years_kept, "0")}
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100] }}>
                      <Chip
                        label={isBlockedByRules(player) ? "Ineligible" : "Eligible"}
                        size="small"
                        title={getBlockedReason(player) || undefined}
                        sx={{
                          backgroundColor: isBlockedByRules(player)
                            ? alpha(colors.redAccent[500], 0.2)
                            : alpha(colors.greenAccent[500], 0.2),
                          color: isBlockedByRules(player)
                            ? colors.redAccent[400]
                            : colors.greenAccent[400],
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[300] }}>
                      {formatValue(player.note)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KeeperTeamDetailModal;